// @ts-nocheck

import {
  ANNOTATION_TYPE,
  Annotations,
  BeatMarkers,
  Grids,
  MOUSE_CURSOR,
  Scroller,
  TitleChart,
  getClosestChannelIndex,
  reCalculateConfig,
} from '@/widgets/ecg-viewer';
import {
  showMessageError,
  Highpass,
  Lowpass,
  getBeatMarkersArr,
  getPQSTObject,
  median,
  optimizeSVG,
} from '@/shared/lib';
import { fabric } from 'fabric';
import _ from 'lodash';
import EcgView from './ecgView';

let ECGChart;

(function () {
  const GAIN_VALUES = [1, 2, 5, 10, 20, 50, 100, 200];
  const SPEED_VALUES = [2.5, 10, 25, 50, 75, 100, 200, 500];
  const ACTION_TYPE = {
    AR_BEAT_MARKER: 'isARingBeatMarker',
    ZOOMING: 'isZooming',
  };
  const textChannel = ['CH1', 'CH2', 'CH3'];
  const backgroundColor = '#fafafa';

  ECGChart = function (
    rawDataFromNetwork: any = [[], [], []],
    config: any,
    beatMarkerArr: any = [],
    annotations: any,
    functionObject: any,
    isDisplayScrollbar: any,
    hiddenChannels = [0, 1, 2],
    eventPos: any,
    missingSampleLength = 0,
    diagnosticLead: any,
    timeObject: any,
    index: any,
    isStreaming: any,
  ) {
    this.offsetX = 0;

    this.isFirstLoading = true;
    this.isInViewAllMode = false;

    this.timeObject = timeObject;
    this.isStreaming = isStreaming;

    this.isDisplayScrollbar = isDisplayScrollbar;

    this.diagnosticLead = diagnosticLead;
    this.missingSampleLength = missingSampleLength;

    this.assignFunction(functionObject);
    this.bindEventListener();
    this.initValue(rawDataFromNetwork, config, beatMarkerArr, hiddenChannels, missingSampleLength);

    this.rawDataFromNetwork = [...rawDataFromNetwork];
    this.rawDataZoom = [...rawDataFromNetwork];

    this.canvasBackground = new fabric.StaticCanvas(`my-canvas1-1-${index}`, {
      renderOnAddRemove: false,
      width: this.config.width,
      height: this.config.height,
    });

    this.exportGridCanvas = new fabric.StaticCanvas(`my-canvas1-2-${index}`, {
      renderOnAddRemove: false,
      width: this.config.width,
      height: this.config.height,
      backgroundColor,
    });

    this.canvasChart = new fabric.Canvas(`my-canvas2-${index}`, {
      renderOnAddRemove: false,
      width: this.config.width,
      height: this.config.height,
      selection: false,
    });

    this.canvasObject = new fabric.Canvas(`my-canvas3-${index}`, {
      renderOnAddRemove: false,
      width: this.config.width,
      height: this.config.height,
      selection: false,
      skipTargetFind: false,
      fireRightClick: true,
    });

    if (this.isDisplayScrollbar) {
      this.canvasScroller = new fabric.Canvas(`my-canvas4-${index}`, {
        renderOnAddRemove: false,
        width: this.config.width,
        height: this.config.scrollerHeight,
        selection: false,
      });
    }

    this.loadFullECG(this.offsetX);

    this.annotation = new Annotations.Annotations(
      this.canvasObject,
      this.config,
      this.verticalChannelPlacement,
    );

    // *: Import objects from JSON
    if (annotations && !_.isEmpty(annotations)) {
      this.importJSONObject(annotations);
    }

    if (eventPos) {
      const { samplingRate, sampleLength } = this.config;
      const startSample = eventPos - samplingRate;
      const endSample = eventPos + samplingRate;
      const isDrawnEventPosition = startSample && endSample && endSample < sampleLength;
      if (isDrawnEventPosition) {
        this.eventPos = eventPos;
        this.drawEventPosition(eventPos, true);
      }
    }

    this.renderAll();

    this.isDown = false;
    this.actionType = null;

    this.isExporting = false;

    if (!this.isStreaming) {
      this.canvasObject.on('mouse:down', (event: any) => {
        this.cvObjMouseDown(event);
      });

      this.canvasObject.on('mouse:move', (event: any) => {
        this.cvObjMouseMove(event);
      });

      this.canvasObject.on('mouse:up', (event: any) => {
        this.cvObjMouseUp(event);
      });

      if (this.isDisplayScrollbar) {
        this.canvasScroller.on('mouse:down', (event: any) => {
          this.cvScMouseDown(event);
        });

        this.canvasScroller.on('mouse:move', (event: any) => {
          this.cvScMouseMove(event);
        });

        this.canvasScroller.on('mouse:up', (event: any) => {
          this.cvScMouseUp(event);
        });

        this.canvasScroller.on('mouse:out', () => {
          this.cvScMouseOut();
        });
      }
    }
  };

  ECGChart.prototype.cvObjMouseDown = function (event: any) {
    // *: De-select object after changing to View All mode
    if (this.isInViewAllMode) {
      this.actionType = null;
      this.annotation.selectedObj = null;
      this.ecgView.specifySampleMark = null;
      this.ecgView.specifyPattern = null;
    }

    const p = this.canvasObject.getPointer(event.e);
    const clickedChannelIndex = getClosestChannelIndex(p.y, this.verticalChannelPlacement);
    const { button } = event;
    this.isDown = true;
    this.isDownLeft = button === 1;

    // *: Calculate sample mark offsetX
    this.clickedOffsetX = Math.round(p.x / this.config.indexToPix) + this.offsetX;

    if (this.actionType) {
      switch (this.actionType) {
        case ANNOTATION_TYPE.HLINE: {
          if (button === 1) {
            this.annotation.update(
              p,
              true,
              this.displayedChannelsArr,
              null,
              null,
              this.markers.beatMarkers,
              this.offsetX,
            );

            if (this.zoomingChannel === null || typeof this.zoomingChannel === 'undefined') {
              this.annotation.updateChannelIndex(this.displayedChannelsArr);
            }

            this.actionType = null;
            this.isHLineDone = true;
            this.lastPointOnMove = null;
            this.scrollDirection = null;
            this.pointOnMove = null;
            this.haveEntered = false;
            this.isScrollingChart = false;
            this.propsFunction.updateSelectingHLineStatus(true);
            this.propsFunction.updateCompletedStatus(false, false);
            window.removeEventListener('keyup', this.eventCancelDrawHLine, false);
            this.isAddedCancelDrawHLineEventListener = false;
          }

          break;
        }

        case ANNOTATION_TYPE.VLINE: {
          if (button === 1) {
            this.annotation.update(
              p,
              true,
              this.displayedChannelsArr,
              null,
              null,
              this.markers.beatMarkers,
              this.offsetX,
            );

            if (this.zoomingChannel === null || typeof this.zoomingChannel === 'undefined') {
              this.annotation.updateChannelIndex(this.displayedChannelsArr);
            }

            this.actionType = null;
            this.isVLineDone = true;
            this.lastPointOnMove = null;
            this.scrollDirection = null;
            this.pointOnMove = null;
            this.haveEntered = false;
            this.isScrollingChart = false;
            this.propsFunction.updateSelectingVLineStatus(true);
            this.propsFunction.updateCompletedStatus(false, false);
            window.removeEventListener('keyup', this.eventCancelDrawVLine, false);
            this.isAddedCancelDrawVLineEventListener = false;
          }

          break;
        }

        case ANNOTATION_TYPE.RECT_SELECT: {
          if (button === 1) {
            const specifyPattern = {
              startSample: Math.min(this.contextMenuSample, this.clickedOffsetX),
              endSample: Math.max(this.contextMenuSample, this.clickedOffsetX),
              channelIndex: this.displayedChannelsArr[this.annotation.selectedObj.channelIndex],
            };
            this.ecgView.drawPattern(this.offsetX, specifyPattern);
            this.annotation.selectedObj.delete();
            _.remove(this.annotation.annoArr, (x) => x.type === ANNOTATION_TYPE.RECT_SELECT);
            this.annotation.selectedObj = null;
            this.actionType = null;
            this.isRectSelectDone = true;
            this.lastPointOnMove = null;
            this.scrollDirection = null;
            this.pointOnMove = null;
            this.haveEntered = false;
            this.isScrollingChart = false;
            this.isDrawingRectSelect = false;
            this.propsFunction.updateCompletedStatus(false, false);
            this.propsFunction.updateSelectingPatternStatus(true);
            this.renderECG();
            window.removeEventListener('keyup', this.eventCancelDrawRectSelect, false);
            this.isAddedCancelDrawRectSelectEventListener = false;
          }

          break;
        }

        case ACTION_TYPE.AR_BEAT_MARKER: {
          if (button === 3) {
            this.markers.delete(p, this.offsetX);
          } else if (button === 1) {
            // *: Add default beat type "N"
            this.markers.add(this.clickedOffsetX, this.offsetX, 1, {
              handleOpenSnackbarARBeat: this.propsFunction.handleOpenSnackbarARBeat,
              handleCloseSnackbarARBeat: this.propsFunction.handleCloseSnackbarARBeat,
            });
          }

          // *: Update belowText after adjusting beat markers
          if (this.annotation.annoArr && this.annotation.annoArr.length) {
            /**
             * The subset of chart annotations that are horizontal caliper beat markers. Filters the annotation array by a type equal to 'HorizontalCaliper'.
             *
             * @returns Annotation entries whose type is HorizontalCaliper.
             */
            const beatMarkersUpdate = _.filter(
              this.annotation.annoArr,
              (x) => x.type === 'HorizontalCaliper',
            );
            if (beatMarkersUpdate && beatMarkersUpdate.length) {
              _.forEach(beatMarkersUpdate, (beatMarkerUpdate) => {
                beatMarkerUpdate.updateBelowText(this.markers.beatMarkers, this.offsetX);
              });
            }
          }

          break;
        }

        case ACTION_TYPE.ZOOMING: {
          if (button === 1) {
            this.zoomEnd = true;
            this.actionType = null;
            this.propsFunction.updateCompletedStatus(false, true);
            this.zoom(clickedChannelIndex, p.x);

            window.removeEventListener('keyup', this.eventCancelZoom, false);
            this.isAddedCancelZoomEventListener = false;
          }

          break;
        }

        default: {
          break;
        }
      }
    } else if (button === 1) {
      // *: Prevent select objects when is in View All mode
      if (!this.isInViewAllMode) {
        // *: Select objects
        this.annotation.selectObj(p);
        this.renderECG();

        // *: Click to complete/cancel draw text
        if (!this.annotation.selectedObj && this.isDrawingText) {
          this.isDrawingText = false;
          window.removeEventListener('keyup', this.eventCancelDrawText, false);
          this.isAddedCancelDrawTextEventListener = false;
          this.propsFunction.updateCompletedStatus(false, false);
        }

        // *: Select mark
        this.ecgView.selectMark(this.clickedOffsetX, { x: p.x, y: p.y }, clickedChannelIndex);

        // *: Select pattern
        this.ecgView.selectPattern(this.clickedOffsetX, { x: p.x, y: p.y }, clickedChannelIndex);
      }

      // *: Check not click on object/sample mark/pattern when is in view all mode
      if (
        this.isInViewAllMode &&
        !this.annotation.selectedObj &&
        !this.ecgView.specifySampleMark &&
        !this.ecgView.specifyPattern &&
        this.clickedOffsetX <= this.config.sampleLength
      ) {
        this.isClickedViewAll = true;
        this.propsFunction.toggleViewAllMode();
        this.exitViewAllMode();
        this.viewAll();
      }

      this.propsFunction.updateSelectingHLineStatus(
        this.annotation.selectedObj && this.annotation.selectedObj.type === ANNOTATION_TYPE.HLINE,
      );
      this.propsFunction.updateSelectingVLineStatus(
        this.annotation.selectedObj && this.annotation.selectedObj.type === ANNOTATION_TYPE.VLINE,
      );
      this.propsFunction.updateSelectingPatternStatus(this.ecgView.specifyPattern);
    }
  };

  ECGChart.prototype.cvObjMouseMove = function (event: any) {
    if (
      (!this.isDown &&
        !this.isDrawingHLine &&
        !this.isDrawingRectSelect &&
        !this.isDrawingVLine &&
        !this.isARingBeatMarker) ||
      this.isInViewAllMode
    ) {
      return;
    }

    // *: Set pointOnMove value
    if (this.haveEntered) {
      if (this.scrollDirection) {
        this.pointOnMove = this.lastPointOnMove
          ? this.lastPointOnMove
          : this.canvasObject.getPointer(event.e);
        this.pointOnMove.x = this.scrollDirection === 'left' ? 0 : 945;
        this.haveEntered = false;
      } else {
        this.pointOnMove = this.lastPointOnMove
          ? this.lastPointOnMove
          : this.canvasObject.getPointer(event.e);
        this.haveEntered = false;
      }
    } else {
      this.pointOnMove = this.canvasObject.getPointer(event.e);
    }

    const { movementX } = event.e;
    if (this.actionType === ANNOTATION_TYPE.HLINE || this.actionType === ANNOTATION_TYPE.VLINE) {
      this.annotation.update(
        this.pointOnMove,
        true,
        this.displayedChannelsArr,
        null,
        null,
        this.markers.beatMarkers,
        this.offsetX,
      );
    } else if (this.actionType === ANNOTATION_TYPE.RECT_SELECT) {
      this.annotation.update(
        this.pointOnMove,
        true,
        this.displayedChannelsArr,
        null,
        null,
        this.markers.beatMarkers,
        this.offsetX,
      );
    } else if (this.annotation.isModifying) {
      this.annotation.update(
        this.pointOnMove,
        false,
        this.displayedChannelsArr,
        null,
        null,
        this.markers.beatMarkers,
        this.offsetX,
      );

      if (this.zoomingChannel === null || typeof this.zoomingChannel === 'undefined') {
        this.annotation.updateChannelIndex(this.displayedChannelsArr);
      }
    } else if (
      !this.isARingBeatMarker &&
      !this.isRectSelectDone &&
      !this.isHLineDone &&
      !this.isVLineDone &&
      this.isDownLeft
    ) {
      // * Prevent to scroll with right mouse click
      this.isScrollingChart = true;
      this.changeCursor(MOUSE_CURSOR.MOVE);
      this.handleScrollOnMove(movementX);
    }
    // else if (this.ecgView.isClickFirstVLine || this.ecgView.isClickLastVLine) { // *: Edit sample mark
    //   const offsetXVLine = Math.round(movementX / this.config.indexToPix);
    //   this.ecgView.editSampleMark(this.offsetX, offsetXVLine);
    // }
  };

  ECGChart.prototype.cvObjMouseUp = function (event: any) {
    if (this.isInViewAllMode) {
      return;
    }

    if (
      !this.isDrawingHLine &&
      !this.isARingBeatMarker &&
      !this.isDrawingRectSelect &&
      !this.isDrawingVLine
    ) {
      const { button } = event;
      this.isDown = false;

      if (
        !this.isScrollingChart &&
        !this.zoomEnd &&
        button === 1 // *: Only select with left click
      ) {
        // *: Selected sample mark/pattern
        this.ecgView.drawSelectedMark(this.offsetX);
        this.ecgView.drawSelectedPattern(this.offsetX);

        this.pickDate();
      }

      // if (this.ecgView.specifySampleMark && (this.ecgView.isClickFirstVLine || this.ecgView.isClickLastVLine)) {
      //   this.ecgView.updateSpecifySampleMark();
      // }

      this.annotation.isModifying = false;
      this.isScrollingChart = false;
      this.zoomEnd = false;

      // *: Clear data
      this.annotation.prevPoint = null;

      // *: Handle for left click while is in Mode (do not reset to default cursor)
      if (!this.actionType) {
        this.changeCursor(MOUSE_CURSOR.DEFAULT);
      }
    }

    // *: Prevent scroll when is drawing horizontal line/pattern
    if (this.isRectSelectDone) {
      this.isDown = false;
      this.isRectSelectDone = false;
      this.isDrawingRectSelect = false;
    }

    if (this.isHLineDone) {
      this.changeCursor(MOUSE_CURSOR.DEFAULT);
      this.isDown = false;
      this.isHLineDone = false;
      this.isDrawingHLine = false;
    }

    if (this.isVLineDone) {
      this.changeCursor(MOUSE_CURSOR.DEFAULT);
      this.isDown = false;
      this.isVLineDone = false;
      this.isDrawingVLine = false;
    }
  };

  ECGChart.prototype.cvScMouseDown = function (event: any) {
    const { x, y } = event.pointer;
    this.isMouseDownScroller = this.scroller.checkClickInsideSelectionRange(x, y);
  };

  ECGChart.prototype.cvScMouseMove = function (event: any) {
    if (!this.isMouseDownScroller) {
      const { x, y } = event.pointer;
      this.isMouseOverScroller = this.scroller.checkClickInsideSelectionRange(x, y);
      this.isMouseUpScroller = this.isMouseOverScroller;
      if (this.isMouseOverScroller && !this.isDrawScrollerTrue) {
        this.scroller.draw(this.offsetX, true);
        this.isDrawScrollerTrue = true;
        this.isDrawScrollerFalse = false;
      }

      if (!this.isMouseOverScroller && !this.isDrawScrollerFalse) {
        this.scroller.draw(this.offsetX, false);
        this.isDrawScrollerFalse = true;
        this.isDrawScrollerTrue = false;
      }

      return;
    }

    this.isScrollingSelectionRange = true;

    const { movementX } = event.e;
    const { maxIndex, minIndex, width, sampleLength } = this.config;

    this.offsetX += Math.round((movementX * sampleLength) / width);
    this.offsetX = this.offsetX < maxIndex ? this.offsetX : maxIndex;
    this.offsetX = this.offsetX > minIndex ? this.offsetX : minIndex;

    this.annotation.scroll(this.offsetX, this.displayedChannelsArr);

    this.loadECG(this.offsetX);
    this.renderECG();
  };

  ECGChart.prototype.cvScMouseUp = function (event: any) {
    const { x, y } = event.pointer;
    const { sampleLength, sampleRange, maxIndex, minIndex, width } = this.config;

    this.isMouseDownScroller = false;

    if (!this.isScrollingSelectionRange) {
      // *: Do not re-draw scroller after scroll selection range
      this.isMouseUpScroller = true;

      this.offsetX = Math.round((x * sampleLength) / width - sampleRange / 2);
      this.offsetX = this.offsetX < maxIndex ? this.offsetX : maxIndex;
      this.offsetX = this.offsetX > minIndex ? this.offsetX : minIndex;

      this.annotation.scroll(this.offsetX, this.displayedChannelsArr);

      this.loadECG(this.offsetX);
      this.renderECG();
    } else {
      this.isScrollingSelectionRange = false;
      this.isMouseUpScroller = this.scroller.checkClickInsideSelectionRange(x, y);
      this.isMouseOverScroller = this.isMouseUpScroller;
      this.isDrawScrollerFalse = false;
      this.isDrawScrollerTrue = false;

      this.loadECG(this.offsetX);
      this.renderECG();
    }
  };

  ECGChart.prototype.cvScMouseOut = function () {
    if (!this.isScrollingSelectionRange) {
      this.isMouseUpScroller = false;
      this.isMouseOverScroller = false;
      this.isMouseDownScroller = false;
      this.isDrawScrollerFalse = false;
      this.isDrawScrollerTrue = false;
      this.scroller.draw(this.offsetX, false);
    }
  };

  ECGChart.prototype.importJSONObject = function (objectArray: any) {
    _.forEach(objectArray, (object) => {
      this.annotation.scroll(this.offsetX, this.displayedChannelsArr);

      if (object.type === ANNOTATION_TYPE.SAMPLE_MARK || object.type === ANNOTATION_TYPE.PATTERN) {
        this.ecgView.importMark(this.offsetX, object);
      } else if (object.type === ANNOTATION_TYPE.BEAT_MARKER) {
        this.beatMarkers = JSON.parse(object.beatMarkers);
      } else {
        this.annotation.import(object);
      }
    });
    this.updateConfig();
  };

  ECGChart.prototype.loadECG = function (offsetX: any) {
    if (!this.ecgView) {
      // oxlint-disable-next-line import/no-named-as-default-member
      this.ecgView = new EcgView.EcgView(
        this.canvasChart,
        this.pixelSamples,
        this.config,
        this.displayedChannelsArr,
        this.verticalChannelPlacement,
        this.isStreaming,
        this.propsFunction.getStreamingData,
        this.propsFunction.onBpmChanged,
      );
    }
    if (this.isStreaming && this.isFirstLoading) {
      this.ecgView.drawStreaming(offsetX);
      this.isFirstLoading = false;
    }

    if (!this.isStreaming) {
      this.ecgView.draw(offsetX);
    }

    if (this.isDisplayScrollbar) {
      if (!this.scroller) {
        this.scroller = new Scroller.Scroller(
          this.canvasScroller,
          this.samplesScroller,
          this.config,
          this.config.prevChanIdx,
        );
      }
      this.scroller.draw(
        offsetX,
        this.isMouseOverScroller || this.isMouseDownScroller || this.isMouseUpScroller,
      );
    }

    if (!this.markers) {
      this.markers = new BeatMarkers.BeatMarkers({
        canvas: this.canvasObject,
        beatMarkers: this.beatMarkers,
        verticalChannelPlacement: this.verticalChannelPlacement,
        config: this.config,
      } as any);
    }

    // if (this.beatMarkers && this.beatMarkers.length) {
    //   this.markers.draw(offsetX, this.isInViewAllMode);
    // }
    this.markers.draw(offsetX, this.isInViewAllMode);
    this.propsFunction.updateMinMaxHr(this.markers.getMinMaxHr());

    if (!this.titleChart) {
      this.titleChart = new TitleChart.TitleChart({
        canvas: this.canvasObject,
        verticalChannelPlacement: this.verticalChannelPlacement,
        config: this.config,
        displayedChannelsArr: this.displayedChannelsArr,
        timeObject: this.timeObject,
        isStreaming: this.isStreaming,
      } as any);
    } else {
      this.titleChart.update({
        verticalChannelPlacement: this.verticalChannelPlacement,
        config: this.config,
        displayedChannelsArr: this.displayedChannelsArr,
      });
    }
    this.titleChart.draw(offsetX);
    this.titleChart.displayForViewLiveEcg({
      isVisibilityChannel: true,
    });
  };

  ECGChart.prototype.loadFullECG = function (offsetX: any) {
    if (!this.gridBackGround) {
      const configGrid = {
        width: this.config.width,
        height: this.config.height,
        majorDivSize: this.config.majorDivSize,
      };
      this.gridBackGround = new Grids.Grid(this.canvasBackground, configGrid);
    }

    if (!this.exportGrid) {
      const configGrid = {
        width: this.config.width,
        height: this.config.height,
        majorDivSize: this.config.exportMajorDivSize,
      };
      this.exportGrid = new Grids.Grid(this.exportGridCanvas, configGrid);
    }
    this.exportGrid.drawExportGrid();

    // *: Do not draw grid when in View All Mode
    if (!this.isInViewAllMode) {
      this.gridBackGround.draw();
    } else {
      this.gridBackGround.drawBorder();
    }

    this.loadECG(offsetX);
  };

  ECGChart.prototype.renderECG = function () {
    this.canvasObject.renderAll();
  };

  ECGChart.prototype.renderAll = function () {
    this.exportGridCanvas.renderAll();
    this.renderECG();
  };

  ECGChart.prototype.cleanupData = function () {
    this.samples = null;
    this.pixelSamples = null;
    this.rawDataFromNetwork = null;
    this.rawData = null;
    this.rawDataZoom = null;
    this.originalSamples = null;

    this.canvasBackground.clear();
    this.canvasBackground = null;
    this.canvasChart.clear();
    this.canvasChart.remove(...this.canvasChart.getObjects());
    this.canvasChart = null;
    this.canvasObject.clear();
    this.canvasObject.remove(...this.canvasObject.getObjects());
    this.canvasObject = null;

    if (this.isDisplayScrollbar) {
      this.samplesScroller = null;
      this.canvasScroller.clear();
      this.canvasScroller.remove(...this.canvasScroller.getObjects());
      this.canvasScroller = null;
    }

    this.exportGridCanvas.clear();
    this.exportGridCanvas.remove(...this.exportGridCanvas.getObjects());
    this.exportGridCanvas = null;
    this.ecgView = null;
    this.annotation = null;
    this.removeAllEventListener();
  };

  ECGChart.prototype.initValue = function (
    rawDataFromNetwork: any,
    config: any,
    beatMarkerArr: any,
    hiddenChannels: any,
    missingSampleLength: any,
  ): any {
    if (rawDataFromNetwork && rawDataFromNetwork.length) {
      this.displayedChannelsArr = [];
      _.forEach(hiddenChannels, (hiddenChannel) => {
        this.displayedChannelsArr.push(hiddenChannel);
      });

      this.rawDataFromNetwork = [...rawDataFromNetwork];
      this.rawData = this.calculateRawDataFromNetwork(rawDataFromNetwork);
      this.samples = [];
      this.B0 = [];
      this.pixelSamples = [];
      this.verticalChannelPlacement = [];

      this.config = {
        samplingRate: 250,
        numberOfVisibleChannels: rawDataFromNetwork.length,
        defaultSpeed: 25.0,
        gain: 10.0,
        adcZero: 0,
        adcGain: 655.35875,
        someChannelsHidden: this.displayedChannelsArr.length !== 3,
        researchMode: false,
        LFF: 0,
        HFF: 0,
        hffOrder: 8,
        bccHFF: 15,
        bccMinSR: 200,
        defaultWidth: 945,
        width: 945,
        defaultHeight: 250,
        height: 250,
        lineHeight: 250 / 3,
        defaultWndDuration: 10,
        aspectRatioX: 3.78,
        aspectRatioY: 3.78,
        minIndex: 0,
        sampleLength: rawDataFromNetwork[0].length,
        prevChanIdx: 1,
        exportWndDuration: 10,
        exportSpeed: 25,
        exportGain: 10,
        exportFineGrid: false,
        exportNumberOfVisibleChannels: rawDataFromNetwork.length,
      };

      this.config.recDuration =
        this.rawData.length / this.config.samplingRate / this.config.numberOfVisibleChannels;
      this.viewAllProportion = this.config.recDuration / 10;
      this.config.speed = this.config.defaultSpeed;
      this.config.wndDuration = this.config.defaultWndDuration;

      if (this.isDisplayScrollbar) {
        this.config.scrollerHeight = 31;
        this.config.defaultScrollerHeight = 31;
      }

      this.exportDisplayedChannelsArr = this.displayedChannelsArr;

      this.isInViewAllMode = false;

      if (config) {
        this.config = _.assign(this.config, { ...config });
      }

      this.beatMarkers = beatMarkerArr;

      this.calculateConfig();
      this.calculateSamplesFromRawData(this.rawData, missingSampleLength);
      this.propsFunction.updateSpdMenu(this.config.speed);
    } else {
      this.config = {
        samplingRate: 250,
        numberOfVisibleChannels: 0,
        defaultSpeed: 25.0,
        gain: 10.0,
        adcZero: 0,
        adcGain: 655.35875,
        someChannelsHidden: false,
        researchMode: false,
        LFF: 0,
        HFF: 0,
        hffOrder: 8,
        bccHFF: 15,
        bccMinSR: 200,
        defaultWidth: 945,
        width: 945,
        defaultHeight: 250,
        height: 250,
        lineHeight: 250 / 3,
        defaultWndDuration: 10,
        aspectRatioX: 3.78,
        aspectRatioY: 3.78,
        minIndex: 0,
        sampleLength: 0,
        prevChanIdx: 1,
        exportWndDuration: 10,
        exportSpeed: 25,
        exportGain: 10,
        exportNumberOfVisibleChannels: 0,
        exportFineGrid: false,
      };

      this.config.recDuration = 10;
      this.viewAllProportion = this.config.recDuration / 10;
      this.config.speed = this.config.defaultSpeed;
      this.config.wndDuration = this.config.defaultWndDuration;

      if (this.isDisplayScrollbar) {
        this.config.scrollerHeight = 31;
        this.config.defaultScrollerHeight = 31;
      }

      this.exportDisplayedChannelsArr = this.displayedChannelsArr;

      const objectConfigUpdate = reCalculateConfig(this.config);
      const channelLabels: any = [];
      this.config = { ...this.config, ...objectConfigUpdate, channelLabels };
    }
  };

  ECGChart.prototype.calculateConfig = function () {
    const channelLabels = new Array(this.displayedChannelsArr.length);
    _.forEach(this.displayedChannelsArr, (channel, index) => {
      channelLabels[index] = `${textChannel[channel]}`;
    });
    const objectConfigUpdate = reCalculateConfig(this.config);
    this.config = { ...this.config, ...objectConfigUpdate, channelLabels };
  };

  ECGChart.prototype.calculateRawDataFromNetwork = function (rawDataFromNetwork: any) {
    if (rawDataFromNetwork && rawDataFromNetwork.length) {
      const rawData = [];
      for (let i = 0; i < rawDataFromNetwork[0].length; i += 1) {
        for (let j = 0; j < rawDataFromNetwork.length; j += 1) {
          rawData.push(rawDataFromNetwork[j][i]);
        }
      }
      return rawData;
    }
    return [[], [], []];
  };

  ECGChart.prototype.calculatePixelSamplesFromSamples = function (samples: any) {
    const { height, numberOfVisibleChannels, millivoltToPix, indexToPix } = this.config;

    this.verticalChannelPlacement = samples.map((chArr: any, chIdx: any) => {
      if (chIdx !== samples.length - 1) {
        return (height / numberOfVisibleChannels) * (chIdx + 0.5) + 18.9; // *: Translate 1 default square
      }

      return (height / numberOfVisibleChannels) * (chIdx + 0.5);
    });

    this.pixelSamples = samples.map((chArr: any, chIdx: any) =>
      chArr.map((u: any, i: any) => ({
        x: Math.floor(i * indexToPix),
        y: Math.floor(this.verticalChannelPlacement[chIdx] - u * millivoltToPix),
      })),
    );
    const bpm = this.calculateBpm();
    this.propsFunction.onCalculatedData({ data: this.pixelSamples, bpm });
  };

  ECGChart.prototype.calculateBpm = function () {
    const { samplingRate, adcGain } = this.config;

    const diagnosisLeadChannelADC = [];
    for (let i = this.diagnosticLead - 1; i < this.rawData.length; i += 3) {
      diagnosisLeadChannelADC.push(this.rawData[i]);
    }

    // *: Remove sinewave was added to missing data and fill it to null before calculating beat markers
    const clonedDiagnosisLeadChannelADC = [...diagnosisLeadChannelADC];
    const removedSineWaveDataArray = clonedDiagnosisLeadChannelADC.splice(this.missingSampleLength);
    const filledNullMissingSampleDataArray = new Array(this.missingSampleLength).fill(null);
    const expectedDiagnosisLeadChannelADC = [
      ...filledNullMissingSampleDataArray,
      ...removedSineWaveDataArray,
    ];
    const isHighSamplingRate = samplingRate === 1000;
    const beatMarkers = isHighSamplingRate
      ? []
      : getBeatMarkersArr(samplingRate, adcGain, expectedDiagnosisLeadChannelADC);

    const arrayList = [0, 0, 0];
    if (beatMarkers.length < 2) {
      return arrayList[1];
    }

    const listAvg = [];
    for (let i = 0; i < beatMarkers.length - 1; i += 1) {
      const avg = Math.round((60 * samplingRate) / (beatMarkers[i] - beatMarkers[i - 1]));
      listAvg.push(avg);
    }

    // remove 2 first item because it's not stable
    listAvg.shift();
    listAvg.shift();

    let sum = 0;
    _.forEach(listAvg, (bpm) => {
      sum += bpm;
    });

    const avg = Math.round(sum / listAvg.length);
    const max = _.max(listAvg);
    const min = _.min(listAvg);

    arrayList[0] = min;
    arrayList[1] = avg;
    arrayList[2] = max;

    return arrayList[1];
  };

  ECGChart.prototype.calculateSamplesFromRawData = function (
    rawData: any,
    missingSampleLength: any,
  ) {
    const { samplingRate, numberOfVisibleChannels, adcZero, adcGain, someChannelsHidden } =
      this.config;

    /**
     * Physical millivolt samples converted from the raw ADC waveform buffer. Each raw sample is offset by the ADC zero baseline and divided by the ADC gain.
     *
     * @returns The waveform samples in physical units.
     */
    const samples = rawData.map((n: any) => (n - adcZero) / adcGain);
    const samplesPerChannel = rawData.length / numberOfVisibleChannels;
    this.samples = new Array(numberOfVisibleChannels);

    for (let j = 0; j < this.samples.length; j += 1) {
      this.samples[j] = new Array(samplesPerChannel);
    }

    samples.reduce((allChannels: any, newSample: any, index: any) => {
      allChannels[index % numberOfVisibleChannels][Math.floor(index / numberOfVisibleChannels)] =
        newSample;
      return allChannels;
    }, this.samples);

    this.B0 = this.samples.map(median);
    this.samples.forEach((chArr: any, chIdx: any) => {
      for (let j = 0; j < chArr.length; j += 1) {
        chArr[j] -= this.B0[chIdx];
      }
    });

    this.originalSamples = this.samples.map((chArr: any) => chArr.slice());

    if (this.isDisplayScrollbar) {
      this.samplesScroller = this.samples.map((chArr: any) => chArr.slice());
    }

    if (someChannelsHidden) {
      this.samples = this.displayedChannelsArr.map((chIdx: any) =>
        this.originalSamples[chIdx].slice(),
      );
      this.config.numberOfVisibleChannels = this.samples.length;
    }

    this.filterSamples(false);
    this.calculatePixelSamplesFromSamples(this.samples);

    if (this.isStreaming) {
      return;
    }

    // const firstChannelADC = rawData.filter((pt, index) => (index % numberOfVisibleChannels === 0));
    const diagnosisLeadChannelADC = [];
    for (let i = this.diagnosticLead - 1; i < rawData.length; i += 3) {
      diagnosisLeadChannelADC.push(rawData[i]);
    }

    // *: Remove sinewave was added to missing data and fill it to null before calculating beat markers
    const clonedDiagnosisLeadChannelADC = [...diagnosisLeadChannelADC];
    const removedSineWaveDataArray = clonedDiagnosisLeadChannelADC.splice(missingSampleLength);
    const filledNullMissingSampleDataArray = new Array(missingSampleLength).fill(null);
    const expectedDiagnosisLeadChannelADC = [
      ...filledNullMissingSampleDataArray,
      ...removedSineWaveDataArray,
    ];

    if (typeof this.beatMarkers === 'undefined' || !this.beatMarkers || !this.beatMarkers.length) {
      this.beatMarkers = getBeatMarkersArr(samplingRate, adcGain, expectedDiagnosisLeadChannelADC);
    }
  };

  ECGChart.prototype.reCalculateSamples = function () {
    const { someChannelsHidden } = this.config;

    if (someChannelsHidden) {
      this.samples = this.displayedChannelsArr.map((chIdx: any) =>
        this.originalSamples[chIdx].slice(),
      );

      this.filterSamples(false);

      this.config.numberOfVisibleChannels = this.samples.length;
    } else {
      this.filterSamples(true);

      this.config.numberOfVisibleChannels = this.samples.length;
    }

    this.calculateConfig();
    this.calculatePixelSamplesFromSamples(this.samples);
  };

  ECGChart.prototype.reCalculatePixelSamples = function () {
    this.calculateConfig();

    const { maxIndex, minIndex, prevChanIdx } = this.config;

    this.calculatePixelSamplesFromSamples(this.samples);

    this.gridBackGround.update(this.config);
    this.exportGrid.update(this.config);
    this.ecgView.update(
      this.pixelSamples,
      this.config,
      this.displayedChannelsArr,
      this.verticalChannelPlacement,
    );
    this.markers.update({
      beatMarkers: this.beatMarkers,
      verticalChannelPlacement: this.verticalChannelPlacement,
      config: this.config,
    });
    this.propsFunction.updateMinMaxHr(this.markers.getMinMaxHr());

    if (this.isDisplayScrollbar) {
      this.scroller.update(this.samplesScroller, this.config, prevChanIdx);
    }

    this.titleChart.update({
      verticalChannelPlacement: this.verticalChannelPlacement,
      config: this.config,
      displayedChannelsArr: this.displayedChannelsArr,
    });
    this.titleChart.displayForViewLiveEcg({
      isVisibilityChannel: true,
    });

    this.offsetX = this.offsetX < maxIndex ? this.offsetX : maxIndex;
    this.offsetX = this.offsetX > minIndex ? this.offsetX : minIndex;

    this.annotation.updateConfig(
      this.config,
      this.offsetX,
      this.verticalChannelPlacement,
      this.displayedChannelsArr,
    );

    this.loadFullECG(this.offsetX);
    this.renderAll();
  };

  ECGChart.prototype.assignFunction = function (functionObject: any) {
    this.propsFunction = {};
    this.propsFunction.updateMinMaxHr = functionObject.updateMinMaxHr;
    this.propsFunction.updatePrevChanIdx = functionObject.updatePrevChanIdx;
    this.propsFunction.updateVisibleChannels = functionObject.updateVisibleChannels;
    this.propsFunction.updateSpdMenu = functionObject.updateSpdMenu;
    this.propsFunction.updateAmpMenu = functionObject.updateAmpMenu;
    this.propsFunction.handleChangeHiLoPDropdown = functionObject.handleChangeHiLoPDropdown;
    this.propsFunction.handleToggleFineGrid = functionObject.handleToggleFineGrid;
    this.propsFunction.handleARBeat = functionObject.handleARBeat;
    this.propsFunction.handleZoom = functionObject.handleZoom;
    this.propsFunction.handleViewAll = functionObject.handleViewAll;
    this.propsFunction.toggleViewAllMode = functionObject.toggleViewAllMode;
    this.propsFunction.updateCompletedStatus = functionObject.updateCompletedStatus;
    this.propsFunction.updateWaitingZoomModeStatus = functionObject.updateWaitingZoomModeStatus;
    this.propsFunction.updateZoomModeStatus = functionObject.updateZoomModeStatus;
    this.propsFunction.updateARBeatModeStatus = functionObject.updateARBeatModeStatus;
    this.propsFunction.updateSelectingHLineStatus = functionObject.updateSelectingHLineStatus;
    this.propsFunction.updateSelectingVLineStatus = functionObject.updateSelectingVLineStatus;
    this.propsFunction.updateSelectingPatternStatus = functionObject.updateSelectingPatternStatus;
    this.propsFunction.handleOpenSnackbarARBeat = functionObject.handleOpenSnackbarARBeat;
    this.propsFunction.handleCloseSnackbarARBeat = functionObject.handleCloseSnackbarARBeat;
    this.propsFunction.handlePickDate = functionObject.handlePickDate;
    this.propsFunction.onCalculatedData = functionObject.onCalculatedData;
    this.propsFunction.getStreamingData = functionObject.getStreamingData;
    this.propsFunction.onBpmChanged = functionObject.onBpmChanged;
  };

  ECGChart.prototype.bindEventListener = function () {
    this.eventCancelDrawHLine = this.handleCancelDrawHLine.bind(this);
    this.eventCancelDrawVLine = this.handleCancelDrawVLine.bind(this);
    this.eventCancelDrawText = this.handleCancelDrawText.bind(this);
    this.eventCancelARBeatMarker = this.handleCancelARBeatMarker.bind(this);
    this.eventCancelZoom = this.handleCancelZoom.bind(this);
    this.eventCancelDrawRectSelect = this.handleCancelDrawRectSelect.bind(this);
    this.eventExitZoom = this.handleExitZoom.bind(this);
  };

  ECGChart.prototype.updateData = function (
    rawDataFromNetwork: any = [[], [], []],
    config: any,
    beatMarkerArr: any = [],
    annotations: any,
    isDisplayScrollbar: any,
    hiddenChannels = [0, 1, 2],
    eventPos: any,
    missingSampleLength = 0,
    diagnosticLead: any,
  ) {
    this.offsetX = 0;

    this.isDisplayScrollbar = isDisplayScrollbar;

    this.diagnosticLead = diagnosticLead;

    this.missingSampleLength = missingSampleLength;

    this.removeAllEventListener();

    this.changeCursor(MOUSE_CURSOR.DEFAULT);

    this.initValue(rawDataFromNetwork, config, beatMarkerArr, hiddenChannels, missingSampleLength);

    // TODO: Should we use it?
    // this.isInViewAllMode = true;

    this.canvasBackground.clear();

    // *: Delete all annotations
    _.forEach(this.annotation.annoArr, (anno) => {
      this.annotation.selectObjByInstance(anno);
      this.annotation.deleteObj(true);
      if (anno.type === ANNOTATION_TYPE.EVENT_POSITION) {
        this.canvasObject.remove(anno.l, anno.rect);
      }
    });
    this.annotation.annoArr = [];

    if (this.annotation.selectedObj) {
      this.annotation.selectedObj = null;
    }

    this.ecgView.updateData();

    this.zoomingChannel = null;
    this.isDown = false;
    this.actionType = null;
    this.isARingBeatMarker = false;
    this.isInWaitingZoomMode = false;
    this.isAddedCancelZoomEventListener = false;

    if (this.isDisplayScrollbar) {
      this.isMouseDownScroller = false;
      this.isMouseOverScroller = false;
      this.isMouseUpScroller = false;
    }

    this.isDrawingHLine = false;
    this.isDrawingVLine = false;
    this.isDrawingRectSelect = false;
    this.isDrawingText = false;
    this.isDrawingBCC = false;

    this.propsFunction.updateSpdMenu(this.config.speed);
    this.propsFunction.updateAmpMenu(this.config.gain);
    this.propsFunction.handleChangeHiLoPDropdown(true, this.config.LFF);
    this.propsFunction.handleChangeHiLoPDropdown(false, this.config.HFF);
    this.updateConfig();

    if (eventPos) {
      const { samplingRate, sampleLength } = this.config;
      const startSample = eventPos - samplingRate;
      const endSample = eventPos + samplingRate;
      const isDrawnEventPosition = startSample && endSample && endSample < sampleLength;
      if (isDrawnEventPosition) {
        this.eventPos = eventPos;
        this.drawEventPosition(eventPos, true);
      }
    }

    if (annotations && !_.isEmpty(annotations)) {
      this.importJSONObject(annotations);
    }
  };

  ECGChart.prototype.updateConfig = function () {
    this.reCalculateSamples();

    const { maxIndex, minIndex, prevChanIdx, isShowBeatMarkers } = this.config;

    // *: Update grid after exit View All Mode
    if (!this.isInViewAllMode) {
      this.gridBackGround.update(this.config);
      this.exportGrid.update(this.config);
    }

    this.ecgView.update(
      this.pixelSamples,
      this.config,
      this.displayedChannelsArr,
      this.verticalChannelPlacement,
    );
    this.markers.update({
      beatMarkers: this.beatMarkers,
      verticalChannelPlacement: this.verticalChannelPlacement,
      config: this.config,
    });

    this.propsFunction.updateMinMaxHr(this.markers.getMinMaxHr());

    if (this.isDisplayScrollbar) {
      this.scroller.update(this.samplesScroller, this.config, prevChanIdx);
    }

    this.titleChart.update({
      verticalChannelPlacement: this.verticalChannelPlacement,
      config: this.config,
      displayedChannelsArr: this.displayedChannelsArr,
    });
    this.titleChart.displayForViewLiveEcg({
      isVisibilityChannel: true,
    });

    // *: Re-calculate offsetX depend on clickedOffsetX after exit View All Mode
    if (this.isClickedViewAll) {
      this.isClickedViewAll = false;
      this.offsetX = this.clickedOffsetX - Math.round(this.config.sampleRange / 2);
    }

    if (!this.isExportingAllStrip) {
      this.offsetX = this.offsetX < maxIndex ? this.offsetX : maxIndex;
      this.offsetX = this.offsetX > minIndex ? this.offsetX : minIndex;
    }

    this.annotation.updateConfig(
      this.config,
      this.offsetX,
      this.verticalChannelPlacement,
      this.displayedChannelsArr,
    );

    // *: Show all beat markers before exporting image
    if (this.config.speed === 2.5 || this.isInViewAllMode) {
      this.showHideBeatMarkers(this.isExporting);
    } else {
      this.showHideBeatMarkers(isShowBeatMarkers);
    }

    this.loadFullECG(this.offsetX);
    this.renderAll();
  };

  ECGChart.prototype.showHideBeatMarkers = function (
    isShowBeatMarkers: any,
    shouldUpdateConfig = false,
  ) {
    if (shouldUpdateConfig) {
      this.config.isShowBeatMarkers = isShowBeatMarkers;
    }
    this.markers.setBeatMarkersVisibility(isShowBeatMarkers);
    this.renderECG();
  };

  ECGChart.prototype.removeAllEventListener = function () {
    if (this.isAddedCancelARBeatMarkerEventListener) {
      window.removeEventListener('keyup', this.eventCancelARBeatMarker, false);
      this.isAddedCancelARBeatMarkerEventListener = false;
    }

    if (this.isAddedCancelDrawHLineEventListener) {
      window.removeEventListener('keyup', this.eventCancelDrawHLine, false);
      this.isAddedCancelDrawHLineEventListener = false;
    }

    if (this.isAddedCancelDrawRectSelectEventListener) {
      window.removeEventListener('keyup', this.eventCancelDrawRectSelect, false);
      this.isAddedCancelDrawRectSelectEventListener = false;
    }

    if (this.isAddedCancelDrawTextEventListener) {
      window.removeEventListener('keyup', this.eventCancelDrawText, false);
      this.isAddedCancelDrawTextEventListener = false;
    }

    if (this.isAddedCancelZoomEventListener) {
      window.removeEventListener('keyup', this.eventCancelZoom, false);
      this.isAddedCancelZoomEventListener = false;
    }

    if (this.isAddedExitZoomEventListener) {
      window.removeEventListener('keyup', this.eventExitZoom, false);
      this.isAddedExitZoomEventListener = false;
    }

    if (this.scrollInterval) {
      clearInterval(this.scrollInterval);
      this.scrollInterval = null;
    }
  };

  ECGChart.prototype.getDrawingHLineStatus = function () {
    return this.isDrawingHLine;
  };

  ECGChart.prototype.getDrawingVLineStatus = function () {
    return this.isDrawingVLine;
  };

  ECGChart.prototype.getSelectingStatus = function () {
    return (
      this.ecgView.specifySampleMark || this.annotation.selectedObj || this.ecgView.specifyPattern
    );
  };

  ECGChart.prototype.getViewAllModeStatus = function () {
    return this.isInViewAllMode;
  };

  ECGChart.prototype.getWidgets = function () {
    const annoArr = this.annotation.getJSONArr();
    const viewArr = this.ecgView.getJSONObject();
    return [...annoArr, ...viewArr];
  };

  ECGChart.prototype.getBeatMarkers = function () {
    return this.markers.beatMarkers;
  };

  ECGChart.prototype.getWndDuration = function () {
    return this.config.wndDuration;
  };

  ECGChart.prototype.deleteObject = function () {
    if (
      this.ecgView.specifySampleMark ||
      this.annotation.selectedObj ||
      this.ecgView.specifyPattern
    ) {
      if (this.ecgView.specifySampleMark || this.ecgView.specifyPattern) {
        // *: Delete sample mark or pattern
        this.ecgView.deleteMark();
        if (this.ecgView.specifyPattern) {
          this.propsFunction.updateSelectingPatternStatus(false);
        }
      }

      if (this.annotation.selectedObj) {
        // *: Delete object
        this.annotation.deleteObj();
        if (this.annotation.selectedObj.type === 'HorizontalCaliper') {
          this.propsFunction.updateSelectingHLineStatus(false);
        } else if (this.annotation.selectedObj.type === ANNOTATION_TYPE.VLINE) {
          this.propsFunction.updateSelectingVLineStatus(false);
        }
        this.annotation.selectedObj = null;
      }

      this.actionType = null;
      this.loadECG(this.offsetX);
      this.renderECG();
    }
  };

  ECGChart.prototype.drawEventPosition = function (eventPos: any, isScroll: any) {
    this.annotation.drawEventPosition(eventPos, this.offsetX);

    // *: Jump to the Event position
    if (isScroll) {
      const { samplingRate, wndDuration, width, sampleLength } = this.config;
      const halfWindow = Math.round(((width / 2) * samplingRate * wndDuration) / width);
      if (eventPos <= halfWindow) {
        this.offsetX = 0;
      } else if (eventPos >= sampleLength - halfWindow) {
        this.offsetX = sampleLength - 2 * halfWindow;
      } else {
        this.offsetX = eventPos - halfWindow;
      }
    }
    this.annotation.scroll(this.offsetX, this.displayedChannelsArr);

    this.loadECG(this.offsetX);
  };

  ECGChart.prototype.handleScrollOnMove = function (movementX: any) {
    const { maxIndex, minIndex, sampleRange, width } = this.config;

    this.offsetX -= Math.round((movementX * sampleRange) / width);
    this.offsetX = this.offsetX < maxIndex ? this.offsetX : maxIndex;
    this.offsetX = this.offsetX > minIndex ? this.offsetX : minIndex;

    this.annotation.scroll(this.offsetX, this.displayedChannelsArr);

    this.loadECG(this.offsetX);
    this.renderECG();
  };

  ECGChart.prototype.changeCursor = function (type: any) {
    this.canvasObject.defaultCursor = type;
  };

  ECGChart.prototype.setConfig = function (configObject: any) {
    const {
      speedSetConfig,
      gainSetConfig,
      wndDurationSetConfig,
      majorDivSizeSetConfig,
      fineGridSetConfig,
      numberOfVisibleChannelsSetConfig,
      displayedChannelsArrSetConfig,
    } = configObject;
    this.config.speed = speedSetConfig;
    this.config.gain = gainSetConfig;
    this.config.wndDuration = wndDurationSetConfig;
    this.config.majorDivSize = majorDivSizeSetConfig;
    this.config.fineGrid = fineGridSetConfig;
    this.config.numberOfVisibleChannels = numberOfVisibleChannelsSetConfig;
    this.config.someChannelsHidden = numberOfVisibleChannelsSetConfig.length !== 3;
    this.displayedChannelsArr = displayedChannelsArrSetConfig;
    this.updateConfig();
  };

  ECGChart.prototype.updateSpeed = function (newSpeed: any) {
    const changeProportion = this.config.speed / newSpeed;
    this.config.speed = newSpeed;
    this.config.wndDuration *= changeProportion;

    // *: Cuong test: view all
    // this.config.defaultSpeed = this.config.speed;
    // this.config.defaultWndDuration = this.config.wndDuration;

    if (this.isInWaitingZoomMode) {
      this.startZoom(true);
    }

    this.updateConfig();
  };

  ECGChart.prototype.updateGain = function (newGain: any) {
    this.config.gain = newGain;

    if (this.isInWaitingZoomMode) {
      this.startZoom(true);
    }

    this.updateConfig();
  };

  ECGChart.prototype.updateHighLowPass = function (isHighPass: any, freq: any) {
    const { samplingRate } = this.config;

    if (isHighPass) {
      this.config.LFF = freq;
    } else {
      this.config.HFF = freq;
    }

    this.samples = this.displayedChannelsArr.map((chIdx: any) =>
      this.originalSamples[chIdx].slice(),
    ); // copy original samples

    if (this.isDisplayScrollbar) {
      this.samplesScroller = this.originalSamples.slice();

      for (let i = 0; i < this.samplesScroller.length; i += 1) {
        this.samplesScroller[i] = Highpass(this.samplesScroller[i], this.config.LFF, samplingRate);
        this.samplesScroller[i] = Lowpass(this.samplesScroller[i], this.config.HFF, samplingRate);
      }
    }

    for (let i = 0; i < this.samples.length; i += 1) {
      this.samples[i] = Highpass(this.samples[i], this.config.LFF, samplingRate);
      this.samples[i] = Lowpass(this.samples[i], this.config.HFF, samplingRate);
    }

    this.reCalculatePixelSamples();
  };

  ECGChart.prototype.updatePrevChanIdx = function (prevChanIdx: any) {
    this.config.prevChanIdx = prevChanIdx;

    this.scroller.updatePrevChanIdx(this.samplesScroller, this.offsetX, prevChanIdx);
  };

  ECGChart.prototype.saveContextMenuPosition = function (point: any) {
    const { indexToPix } = this.config;

    this.contextMenuPoint = point;
    this.contextMenuSample = Math.round(point.x / indexToPix) + this.offsetX;
  };

  ECGChart.prototype.drawAnnotation = function (type: any) {
    const { sampleLength } = this.config;

    if (this.contextMenuSample <= sampleLength) {
      this.actionType = type;

      const clickedChannelIndex = getClosestChannelIndex(
        this.contextMenuPoint.y,
        this.verticalChannelPlacement,
      );

      switch (type) {
        case ANNOTATION_TYPE.HLINE: {
          this.changeCursor(MOUSE_CURSOR.CROSSHAIR);

          this.isDrawingHLine = true;

          this.annotation.draw(
            this.offsetX,
            clickedChannelIndex,
            ANNOTATION_TYPE.HLINE,
            this.contextMenuPoint,
          );
          this.annotation.updateChannelIndex(this.displayedChannelsArr);

          window.addEventListener('keyup', this.eventCancelDrawHLine, false);
          this.isAddedCancelDrawHLineEventListener = true;

          break;
        }

        case ANNOTATION_TYPE.VLINE: {
          this.changeCursor(MOUSE_CURSOR.CROSSHAIR);

          this.isDrawingVLine = true;

          this.annotation.draw(
            this.offsetX,
            clickedChannelIndex,
            ANNOTATION_TYPE.VLINE,
            this.contextMenuPoint,
          );
          this.annotation.updateChannelIndex(this.displayedChannelsArr);

          window.addEventListener('keyup', this.eventCancelDrawVLine, false);
          this.isAddedCancelDrawVLineEventListener = true;

          break;
        }

        case ANNOTATION_TYPE.RECT_SELECT: {
          this.isDrawingRectSelect = true;

          this.annotation.draw(
            this.offsetX,
            clickedChannelIndex,
            ANNOTATION_TYPE.RECT_SELECT,
            this.contextMenuPoint,
          );
          this.annotation.updateChannelIndex(this.displayedChannelsArr);

          window.addEventListener('keyup', this.eventCancelDrawRectSelect, false);
          this.isAddedCancelDrawRectSelectEventListener = true;

          break;
        }

        case ANNOTATION_TYPE.TEXT: {
          this.actionType = null;
          this.isDrawingText = true;

          this.annotation.draw(
            this.offsetX,
            clickedChannelIndex,
            ANNOTATION_TYPE.TEXT,
            this.contextMenuPoint,
          );
          this.annotation.updateChannelIndex(this.displayedChannelsArr);

          window.addEventListener('keyup', this.eventCancelDrawText, false);
          this.isAddedCancelDrawTextEventListener = true;

          break;
        }

        case ANNOTATION_TYPE.BCC: {
          this.actionType = null;

          const bccObj = getPQSTObject(
            2500,
            this.contextMenuPoint.x,
            this.displayedChannelsArr[clickedChannelIndex],
            {
              config: this.config,
              // ECGBuffer: this.rawDataZoom && this.rawDataZoom.length ? this.rawDataZoom : this.samples,
              ECGBuffer: this.rawDataZoom,
              ANNBuffer: this.beatMarkers,
              offsetX: this.offsetX,
              isReturnRealValue: !(this.rawDataZoom && this.rawDataZoom.length),
            },
          );

          this.isDrawingBCC = true;

          // *: In case of bccObj is null
          if (!bccObj.P || !bccObj.Q || !bccObj.R || !bccObj.S || !bccObj.T || !bccObj.R2) {
            let r2Manual = null;
            let pManual = null;
            let tManual = null;
            let qManual = null;
            let sManual = null;
            /**
             * The first beat marker at or after the sample where the context menu was opened. Searches the beat-marker list for the earliest sample not smaller than the context-menu sample.
             *
             * @returns Sample position of the located beat marker, or undefined when none is at or after it.
             */
            let rManual = _.find(this.beatMarkers, (x) => x >= this.contextMenuSample);
            if (rManual) {
              /**
               * Position of the manually located R peak inside the beat-marker list. Finds the index of the beat marker whose sample equals the previously resolved manual R sample.
               *
               * @returns Index of the matching beat marker, or -1 when none matches.
               */
              const rIndexManual = _.findIndex(this.beatMarkers, (x) => x === rManual);

              if (rIndexManual === 0) {
                r2Manual = this.beatMarkers[rIndexManual + 1];
                sManual = rManual + (r2Manual - rManual) / 3;
                tManual = rManual + ((r2Manual - rManual) / 3) * 2;
                pManual = r2Manual + 60;
                qManual = pManual + 60;
              } else if (rIndexManual === this.beatMarkers.length - 1) {
                rManual = this.contextMenuSample;
                r2Manual = rManual - 60;
                tManual = r2Manual - 60;
                sManual = tManual - 60;
                qManual = sManual - 60;
                pManual = qManual - 60;
              } else {
                r2Manual = this.beatMarkers[rIndexManual + 1];
                pManual = rManual - 60;
                qManual = rManual - 30;
                sManual = rManual + (r2Manual - rManual) / 3;
                tManual = rManual + ((r2Manual - rManual) / 3) * 2;
              }
            } else {
              rManual = this.contextMenuSample;
              r2Manual = rManual - 60;
              tManual = r2Manual - 60;
              sManual = tManual - 60;
              qManual = sManual - 60;
              pManual = qManual - 60;
            }

            _.assign(bccObj, {
              P: pManual,
              Q: qManual,
              R: rManual,
              R2: r2Manual,
              S: sManual,
              T: tManual,
            });
          }

          this.annotation.draw(
            this.offsetX,
            this.displayedChannelsArr[clickedChannelIndex],
            ANNOTATION_TYPE.BCC,
            this.contextMenuPoint,
            bccObj,
          );

          this.startZoom(true, { speed: 100, gain: 50 });
          this.zoom(clickedChannelIndex, this.contextMenuPoint.x);

          break;
        }

        default: {
          break;
        }
      }
    } else {
      showMessageError({ content: 'Error' });
    }
  };

  ECGChart.prototype.handleDrawHorizCaliperMouseOver = function () {
    if (this.isDrawingHLine || this.isDrawingVLine || this.isDrawingRectSelect) {
      this.isScrollingChart = false;
      this.haveEntered = true;

      clearInterval(this.scrollInterval);
      this.scrollInterval = null;
    }
  };

  ECGChart.prototype.handleDrawHorizCaliperMouseOut = function (props: any) {
    if (this.isDrawingHLine || this.isDrawingVLine || this.isDrawingRectSelect) {
      this.lastPointOnMove = this.pointOnMove;
      this.scrollDirection = undefined;
      this.isScrollingChart = true;

      if (this.isScrollingChart && props.layerY >= 0 && props.layerY <= 245) {
        this.scrollDirection = props.offsetX < this.contextMenuPoint.x ? 'left' : 'right';

        /**
         * Horizontal pixel step applied per scroll tick, signed by the scroll direction. Uses a step of 2 pixels while the sweep speed is below 500 and 5 pixels otherwise, negated when scrolling right.
         *
         * @returns The signed horizontal movement in pixels.
         */
        const movementX =
          this.config.speed < 500
            ? this.scrollDirection === 'right'
              ? -2
              : 2
            : this.scrollDirection === 'right'
              ? -5
              : 5;
        const leValue = this.scrollDirection === 'right' ? 945 : 0;

        this.scrollInterval = setInterval(() => {
          this.annotation.prevPoint = JSON.parse(JSON.stringify(this.lastPointOnMove));
          this.lastPointOnMove.x -= movementX;

          this.handleScrollOnMove(movementX);

          this.annotation.update(
            this.lastPointOnMove,
            true,
            this.displayedChannelsArr,
            true,
            leValue,
            this.markers.beatMarkers,
            this.offsetX,
          );
        }, 1);
      }
    }
  };

  ECGChart.prototype.changeHLineColor = function (color: any) {
    if (this.annotation.selectedObj && this.annotation.selectedObj.type === 'HorizontalCaliper') {
      this.annotation.selectedObj.changeColor(color);
      // this.annotation.selectedObj.deselect();
      // this.annotation.selectedObj = null;
      this.renderECG();
    }
  };

  ECGChart.prototype.changeVLineColor = function (color: any) {
    if (this.annotation.selectedObj && this.annotation.selectedObj.type === ANNOTATION_TYPE.VLINE) {
      this.annotation.selectedObj.changeColor(color);
      // this.annotation.selectedObj.deselect();
      // this.annotation.selectedObj = null;
      this.renderECG();
    }
  };

  ECGChart.prototype.getLineColor = function () {
    if (this.annotation.selectedObj && this.annotation.selectedObj.type === ANNOTATION_TYPE.HLINE) {
      return this.annotation.selectedObj.colorHLine;
    }

    if (this.annotation.selectedObj && this.annotation.selectedObj.type === ANNOTATION_TYPE.VLINE) {
      return this.annotation.selectedObj.colorVLine;
    }

    if (this.ecgView.specifyPattern) {
      return this.ecgView.specifyPattern.color;
    }

    return '';
  };

  ECGChart.prototype.changePatternColor = function (color: any) {
    if (this.ecgView.specifyPattern) {
      this.ecgView.changePatternColor(color, this.offsetX);
    }
  };

  ECGChart.prototype.handleCancelDrawHLine = function (event: any) {
    const { keyCode } = event;
    if (keyCode === 27) {
      if (this.annotation.selectedObj) {
        this.changeCursor(MOUSE_CURSOR.DEFAULT);

        this.canvasObject.remove(
          this.annotation.selectedObj.lt,
          this.annotation.selectedObj.ls,
          this.annotation.selectedObj.le,
          this.annotation.selectedObj.l,
          this.annotation.selectedObj.lt1,
        );
        _.remove(this.annotation.annoArr, (x) => x === this.annotation.selectedObj);

        this.isDrawingHLine = false;
        this.actionType = null;
        this.isScrollingChart = false;
        this.isDown = false;
        this.haveEntered = false;
        this.scrollDirection = undefined;
        this.annotation.selectedObj = null;
        this.lastPointOnMove = null;
        this.pointOnMove = null;

        this.renderECG();

        clearInterval(this.scrollInterval);
      }

      window.removeEventListener('keyup', this.eventCancelDrawHLine, false);
      this.isAddedCancelDrawHLineEventListener = false;

      this.propsFunction.updateCompletedStatus(false, false);
    }
  };

  ECGChart.prototype.handleCancelDrawVLine = function (event: any) {
    const { keyCode } = event;
    if (keyCode === 27) {
      if (this.annotation.selectedObj) {
        this.changeCursor(MOUSE_CURSOR.DEFAULT);

        this.canvasObject.remove(
          this.annotation.selectedObj.lt,
          this.annotation.selectedObj.ls,
          this.annotation.selectedObj.le,
          this.annotation.selectedObj.l,
        );
        _.remove(this.annotation.annoArr, (x) => x === this.annotation.selectedObj);

        this.isDrawingVLine = false;
        this.actionType = null;
        this.isScrollingChart = false;
        this.isDown = false;
        this.haveEntered = false;
        this.scrollDirection = undefined;
        this.annotation.selectedObj = null;
        this.lastPointOnMove = null;
        this.pointOnMove = null;

        this.renderECG();
      }

      window.removeEventListener('keyup', this.eventCancelDrawVLine, false);
      this.isAddedCancelDrawVLineEventListener = false;

      this.propsFunction.updateCompletedStatus(false, false);
    }
  };

  ECGChart.prototype.handleCancelDrawText = function (event: any) {
    const { keyCode } = event;
    if (keyCode === 27) {
      if (
        this.annotation.selectedObj &&
        this.annotation.selectedObj.isDrawing &&
        this.isDrawingText
      ) {
        this.canvasObject.remove(this.annotation.selectedObj.text);
        _.remove(this.annotation.annoArr, (x) => x === this.annotation.selectedObj);

        this.actionType = null;
        this.annotation.selectedObj.isDrawing = false;
        this.annotation.selectedObj = null;
        this.isDrawingText = false;

        this.renderECG();

        this.propsFunction.updateCompletedStatus(false, false);
      }

      window.removeEventListener('keyup', this.eventCancelDrawText, false);
      this.isAddedCancelDrawTextEventListener = false;

      this.propsFunction.updateCompletedStatus(false, false);
    }
  };

  ECGChart.prototype.handleCancelARBeatMarker = function (event: any) {
    const { keyCode } = event;
    if (keyCode === 27) {
      this.changeCursor(MOUSE_CURSOR.DEFAULT);

      this.actionType = null;
      this.isDown = false;
      this.isARingBeatMarker = false;

      this.propsFunction.updateARBeatModeStatus(false);

      window.removeEventListener('keyup', this.eventCancelARBeatMarker, false);
      this.isAddedCancelARBeatMarkerEventListener = false;

      this.propsFunction.updateCompletedStatus(false, false);
    }
  };

  ECGChart.prototype.handleCancelZoom = function (event: any) {
    const { keyCode } = event;
    if (keyCode === 27) {
      this.changeCursor(MOUSE_CURSOR.DEFAULT);

      this.actionType = null;

      this.propsFunction.updateWaitingZoomModeStatus(false);
      this.propsFunction.updateCompletedStatus(false, false);

      window.removeEventListener('keyup', this.eventCancelZoom, false);
      this.isAddedCancelZoomEventListener = false;

      this.propsFunction.updateCompletedStatus(false, false);
    }
  };

  ECGChart.prototype.handleCancelDrawRectSelect = function (event: any) {
    const { keyCode } = event;
    if (keyCode === 27) {
      this.annotation.selectedObj.delete();
      _.remove(this.annotation.annoArr, (x) => x === this.annotation.selectedObj);

      this.annotation.selectedObj = null;
      this.actionType = null;
      this.isDrawingRectSelect = false;
      this.isScrollingChart = false;
      this.isDown = false;
      this.haveEntered = false;
      this.scrollDirection = undefined;
      this.lastPointOnMove = null;
      this.pointOnMove = null;

      this.renderECG();

      clearInterval(this.scrollInterval);

      window.removeEventListener('keyup', this.eventCancelDrawRectSelect, false);
      this.isAddedCancelDrawRectSelectEventListener = false;

      this.propsFunction.updateCompletedStatus(false, false);
    }
  };

  ECGChart.prototype.handleExitZoom = function (event: any) {
    const { keyCode } = event;
    if (keyCode === 27) {
      if (
        !this.isARingBeatMarker &&
        !this.isInWaitingZoomMode &&
        !this.isDrawingHLine &&
        !this.isDrawingVLine &&
        !this.isDrawingRectSelect
      ) {
        this.exitZoom();
      }
    }
  };

  ECGChart.prototype.markASample = function (seconds: any) {
    const { sampleLength } = this.config;
    if (this.contextMenuSample <= sampleLength) {
      const { samplingRate } = this.config;

      const clickedChannelIndex = getClosestChannelIndex(
        this.contextMenuPoint.y,
        this.verticalChannelPlacement,
      );
      const sampleMarkRange = samplingRate * seconds;
      const sampleMark = {
        channelIndex: this.displayedChannelsArr[clickedChannelIndex],
        startSample: this.clickedOffsetX - sampleMarkRange / 2,
        endSample: this.clickedOffsetX + sampleMarkRange / 2 - 1,
        sampleMarkRange,
      };

      this.ecgView.drawSampleMark(this.offsetX, sampleMark);
      this.propsFunction.updateCompletedStatus(false, true);
    } else {
      showMessageError({ content: 'Error' });
    }
  };

  ECGChart.prototype.getVisibleChannels = function () {
    return this.displayedChannelsArr;
  };

  ECGChart.prototype.setVisibleChannels = function (displayedChannelsArr: any) {
    this.displayedChannelsArr = displayedChannelsArr.sort();

    if (!this.isInZoomMode) {
      this.exportDisplayedChannelsArr = this.displayedChannelsArr;
    }

    this.config.someChannelsHidden =
      this.displayedChannelsArr.length !== this.originalSamples.length;

    // TODO: De-select annotation if its channel is hidden (still error with Text & BCC)
    if (this.annotation.selectedObj) {
      this.annotation.selectedObj.deselect();
      this.annotation.selectedObj = null;
      // this.annotation.isModifying = false;
    }

    // *: De-select sample mark if its channel is hidden
    if (this.ecgView.specifySampleMark) {
      this.ecgView.specifySampleMark = null;
    }

    this.updateConfig();

    if (this.isInWaitingZoomMode) {
      this.startZoom(true);
    }
  };

  ECGChart.prototype.filterSamples = function (reset: any) {
    const { LFF, HFF, samplingRate } = this.config;

    // *: Restore samples from originalSamples
    if (reset) {
      this.samples = this.originalSamples.map((chArr: any) => chArr.slice());

      if (this.isDisplayScrollbar) {
        this.samplesScroller = this.originalSamples.slice();
      }
    }

    for (let i = 0; i < this.samples.length; i += 1) {
      this.samples[i] = Highpass(this.samples[i], LFF, samplingRate);
      this.samples[i] = Lowpass(this.samples[i], HFF, samplingRate);
    }

    if (this.isDisplayScrollbar) {
      for (let i = 0; i < this.samplesScroller.length; i += 1) {
        this.samplesScroller[i] = Highpass(this.samplesScroller[i], LFF, samplingRate);
        this.samplesScroller[i] = Lowpass(this.samplesScroller[i], HFF, samplingRate);
      }
    }
  };

  ECGChart.prototype.toggleFineGrid = function () {
    this.config.fineGrid = !this.config.fineGrid;

    this.gridBackGround.update(this.config);
    this.exportGrid.update(this.config);
    this.gridBackGround.draw();
    this.exportGrid.drawExportGrid();
  };

  ECGChart.prototype.enterARBeatMode = function () {
    this.changeCursor(MOUSE_CURSOR.POINTER);

    this.actionType = ACTION_TYPE.AR_BEAT_MARKER;
    this.isARingBeatMarker = true;

    window.addEventListener('keyup', this.eventCancelARBeatMarker, false);
    this.isAddedCancelARBeatMarkerEventListener = true;

    if (this.annotation.selectedObj) {
      this.annotation.selectedObj.deselect();
      this.annotation.selectedObj = null;
    }

    if (this.ecgView.specifySampleMark || this.ecgView.specifyPattern) {
      this.ecgView.specifySampleMark = null;
      this.ecgView.specifyPattern = null;
    }

    this.renderECG();
  };

  ECGChart.prototype.exitARBeatMode = function () {
    this.changeCursor(MOUSE_CURSOR.DEFAULT);

    this.actionType = null;
    this.isDown = false;
    this.isARingBeatMarker = false;
    this.isARBeatMarkerDone = true;

    this.propsFunction.updateARBeatModeStatus(false, true);

    window.removeEventListener('keyup', this.eventCancelARBeatMarker, false);
    this.isAddedCancelARBeatMarkerEventListener = false;
  };

  ECGChart.prototype.startZoom = function (isZoomIn: any, staticValue: any) {
    if (!this.isDrawingBCC) {
      this.actionType = ACTION_TYPE.ZOOMING;
      this.isInWaitingZoomMode = true;

      window.addEventListener('keyup', this.eventCancelZoom, false);
      this.isAddedCancelZoomEventListener = true;
    }

    if (this.zoomingChannel === null || typeof this.zoomingChannel === 'undefined') {
      this.beforeZoomValue = {
        speed: this.config.speed,
        gain: this.config.gain,
        displayedChannelsArr: this.displayedChannelsArr,
        numberOfVisibleChannels: this.config.numberOfVisibleChannels,
        someChannelsHidden: this.config.someChannelsHidden,
        sampleRange: this.config.sampleRange,
        samples: this.samples,
      };
    }

    if (this.annotation.selectedObj) {
      this.annotation.selectedObj.deselect();
      this.annotation.selectedObj = null;
    }

    if (this.ecgView.specifySampleMark || this.ecgView.specifyPattern) {
      this.ecgView.specifySampleMark = null;
      this.ecgView.specifyPattern = null;
    }

    // *: Set new speed & gain follow zoom condition
    if (staticValue) {
      this.config.newSpeed = staticValue.speed;
      this.config.newGain = staticValue.gain;
      this.actionType = null;

      return;
    }

    let speedIdx;
    let gainIdx;
    this.changeCursor(MOUSE_CURSOR.ZOOM_IN);

    // *: Zoom speed index
    for (let i = 0; i < SPEED_VALUES.length; i += 1) {
      if (this.config.speed === SPEED_VALUES[i]) {
        speedIdx = i;

        if (i <= SPEED_VALUES.length - 2) {
          speedIdx += 1;

          break;
        }
      }
    }

    // *: Zoom gain index
    for (let i = 0; i < GAIN_VALUES.length; i += 1) {
      if (this.config.gain === GAIN_VALUES[i]) {
        gainIdx = i;

        if (i <= GAIN_VALUES.length - 3) {
          gainIdx += 2;

          break;
        }
      }
    }

    this.config.newSpeed = SPEED_VALUES[speedIdx];
    this.config.newGain = GAIN_VALUES[gainIdx];

    if (!this.isAddedCancelZoomEventListener) {
      window.addEventListener('keyup', this.eventCancelZoom, false);
      this.isAddedCancelZoomEventListener = true;
    }

    this.renderECG();
  };

  ECGChart.prototype.zoom = function (channelIndex: any, x: any) {
    const { samplingRate, width } = this.config;

    if (this.annotation.selectedObj && this.annotation.selectedObj.type !== ANNOTATION_TYPE.BCC) {
      this.annotation.selectedObj.deselect();
      this.annotation.selectedObj = null;
    }

    this.changeCursor(MOUSE_CURSOR.DEFAULT);

    // TODO: Test 2 below code lines
    this.propsFunction.updateCompletedStatus(false, false);
    this.isDrawingText = false;

    this.isInWaitingZoomMode = false;
    this.isInZoomMode = true;
    this.config.numberOfVisibleChannels = this.rawDataZoom.length;
    this.lastPrevChanIdx = this.config.prevChanIdx;

    const xr = this.offsetX + Math.round((x * samplingRate * this.config.wndDuration) / width);
    const changeProportion = this.config.speed / this.config.newSpeed;
    this.config.speed = this.config.newSpeed;
    this.config.gain = this.config.newGain;
    this.config.wndDuration *= changeProportion;
    const halfWindow = Math.round(((width / 2) * samplingRate * this.config.wndDuration) / width);
    this.offsetX = xr > halfWindow ? xr - halfWindow : 0;

    if (this.zoomingChannel === null || typeof this.zoomingChannel === 'undefined') {
      this.zoomingChannel = channelIndex;
      this.displayedChannelsArr = [this.displayedChannelsArr[channelIndex]];
    }

    this.config.prevChanIdx = this.zoomingChannel;

    this.config.someChannelsHidden = true;

    this.updateConfig();

    this.propsFunction.updateWaitingZoomModeStatus(false);
    this.propsFunction.updateZoomModeStatus(true);
    this.propsFunction.updateSpdMenu(this.config.speed);
    this.propsFunction.updateAmpMenu(this.config.gain);
    this.propsFunction.updateVisibleChannels(this.displayedChannelsArr, this.isDrawingBCC);
    this.propsFunction.updatePrevChanIdx(this.config.prevChanIdx);

    if (!this.isDrawingBCC) {
      window.removeEventListener('keyup', this.eventCancelZoom, false);
      this.isAddedCancelZoomEventListener = false;
    }

    window.addEventListener('keyup', this.eventExitZoom, false);
    this.isAddedExitZoomEventListener = true;
  };

  ECGChart.prototype.exitZoom = function () {
    if (this.annotation.selectedObj && this.annotation.selectedObj.type !== ANNOTATION_TYPE.BCC) {
      this.annotation.selectedObj.deselect();
      this.annotation.selectedObj = null;
    }

    this.propsFunction.updateCompletedStatus(false, false);

    if (this.isDrawingBCC) {
      this.isDrawingBCC = false;
    }

    if (this.zoomingChannel === null || typeof this.zoomingChannel === 'undefined') {
      this.changeCursor(MOUSE_CURSOR.DEFAULT);

      this.actionType = null;
      this.beforeZoomValue = null;
      this.isInWaitingZoomMode = false;

      window.removeEventListener('keyup', this.eventCancelZoom, false);
      this.isAddedCancelZoomEventListener = false;

      return;
    }

    const { sampleRange } = this.config;

    this.offsetX = Math.round(
      this.offsetX - this.beforeZoomValue.sampleRange / 2 + sampleRange / 2,
    );

    this.zoomingChannel = null;
    const changeProportion = this.config.speed / this.beforeZoomValue.speed;
    this.config.wndDuration *= changeProportion;
    this.config.gain = this.beforeZoomValue.gain;
    this.config.speed = this.beforeZoomValue.speed;
    this.config.prevChanIdx = this.lastPrevChanIdx;
    this.config.numberOfVisibleChannels = this.beforeZoomValue.numberOfVisibleChannels;
    this.config.someChannelsHidden = this.beforeZoomValue.someChannelsHidden;
    this.displayedChannelsArr = this.beforeZoomValue.displayedChannelsArr;
    this.beforeZoomValue = null;
    this.sampleRangeZoom = null;
    this.isInZoomMode = false;

    this.propsFunction.updateZoomModeStatus(false);
    this.propsFunction.updateSpdMenu(this.config.speed);
    this.propsFunction.updateAmpMenu(this.config.gain);
    this.propsFunction.updateVisibleChannels(this.displayedChannelsArr);
    this.propsFunction.updatePrevChanIdx(this.config.prevChanIdx);

    this.filterSamples(true);
    this.calculatePixelSamplesFromSamples(this.samples);
    this.updateConfig();

    window.removeEventListener('keyup', this.eventExitZoom, false);
    this.isAddedExitZoomEventListener = false;
  };

  ECGChart.prototype.enterViewAllMode = function () {
    if (this.isInViewAllMode) {
      return;
    }

    if (this.annotation.selectedObj) {
      this.annotation.selectedObj.deselect();
      this.annotation.selectedObj = null;
    }

    if (this.ecgView.specifySampleMark || this.ecgView.specifyPattern) {
      this.ecgView.specifySampleMark = null;
      this.ecgView.specifyPattern = null;
    }

    this.isInViewAllMode = true;

    this.beforeViewAllValue = {
      speed: this.config.speed,
      wndDuration: this.config.wndDuration,
      gain: this.config.gain,
    };

    this.config.speed = this.config.defaultSpeed / this.viewAllProportion;
    this.config.wndDuration = this.config.defaultWndDuration * this.viewAllProportion;

    this.propsFunction.updateSpdMenu(this.config.speed);
    this.canvasBackground.clear();
    this.viewAll();
  };

  ECGChart.prototype.exitViewAllMode = function () {
    if (this.annotation.selectedObj && this.annotation.selectedObj.type !== ANNOTATION_TYPE.BCC) {
      this.annotation.selectedObj.deselect();
      this.annotation.selectedObj = null;
    }

    this.isInViewAllMode = false;

    this.config.speed = this.beforeViewAllValue.speed;
    this.config.wndDuration = this.beforeViewAllValue.wndDuration;

    this.beforeViewAllValue = null;

    this.propsFunction.updateSpdMenu(this.config.speed);
  };

  ECGChart.prototype.viewAll = function () {
    // *: Re-draw Event Position
    /**
     * The annotation that marks the event position on the strip. Searches the annotation array for the first entry whose type is 'EventPosition'.
     *
     * @returns The event-position annotation, or undefined when absent.
     */
    const eventPosition = _.find(this.annotation.annoArr, (x) => x.type === 'EventPosition');
    if (eventPosition) {
      this.canvasObject.remove(eventPosition.l, eventPosition.rect);
      _.remove(this.annotation.annoArr, (x) => x.type === 'EventPosition');

      this.drawEventPosition(this.eventPos, false);
    }

    this.updateConfig();
  };

  ECGChart.prototype.exportAll = function (preventExportAllStripSvg = false) {
    this.isExporting = true;

    const {
      // exportSpeed,
      // exportWndDuration,
      speed,
      gain,
      exportGain,
      wndDuration,
      majorDivSize,
      exportMajorDivSize,
      fineGrid,
      exportFineGrid,
      numberOfVisibleChannels,
      exportNumberOfVisibleChannels,
      isShowBeatMarkers,
      samplingRate,
    } = this.config;

    const currentSpeed = speed;
    const currentGain = gain;
    const currentWndDuration = wndDuration;
    const currentMajorDivSize = majorDivSize;
    const currentFineGrid = fineGrid;
    const currentNumberOfVisibleChannels = numberOfVisibleChannels;
    const currentDisplayedChannelsArr = this.displayedChannelsArr;

    let setConfigObject = {
      speedSetConfig: this.config.defaultSpeed / this.viewAllProportion,
      gainSetConfig: exportGain,
      wndDurationSetConfig: this.config.defaultWndDuration * this.viewAllProportion,
      majorDivSizeSetConfig: exportMajorDivSize,
      fineGridSetConfig: exportFineGrid,
      numberOfVisibleChannelsSetConfig: exportNumberOfVisibleChannels,
      displayedChannelsArrSetConfig: this.exportDisplayedChannelsArr,
    };

    this.isExportingAllStrip = true;

    let allStripSvg = [];
    if (!preventExportAllStripSvg) {
      // *: Set config
      this.setConfig(setConfigObject);

      this.showHideBeatMarkers(false);
      this.exportGrid.showHideGrid(false);

      // *: Export 60s (like View All mode, svg tab)
      allStripSvg = this.ecgView.exportAll(
        this.titleChart,
        this.markers,
        this.annotation,
        this.exportGrid,
        this.canvasObject,
      );
    }

    if (samplingRate !== 1000) {
      this.showHideBeatMarkers(isShowBeatMarkers);
    }
    this.exportGrid.showHideGrid(true);

    this.isExportingAllStrip = false;

    setConfigObject = {
      speedSetConfig: currentSpeed,
      gainSetConfig: currentGain,
      wndDurationSetConfig: currentWndDuration,
      majorDivSizeSetConfig: currentMajorDivSize,
      // speedSetConfig: this.isInViewAllMode ? exportSpeed : currentSpeed,
      // gainSetConfig: this.isInViewAllMode ? exportGain : currentGain,
      // wndDurationSetConfig: this.isInViewAllMode ? exportWndDuration : currentWndDuration,
      // majorDivSizeSetConfig: this.isInViewAllMode ? exportMajorDivSize : currentMajorDivSize,
      fineGridSetConfig: exportFineGrid,
      numberOfVisibleChannelsSetConfig: exportNumberOfVisibleChannels,
      displayedChannelsArrSetConfig: this.exportDisplayedChannelsArr,
    };

    // *: Set config
    this.setConfig(setConfigObject);

    // *: Export all (svg tag)
    const allIndexStripSvg = this.ecgView.exportAll(
      this.titleChart,
      this.markers,
      this.annotation,
      this.exportGrid,
      this.canvasObject,
    );

    this.isExporting = false;

    setConfigObject = {
      speedSetConfig: currentSpeed,
      gainSetConfig: currentGain,
      wndDurationSetConfig: currentWndDuration,
      majorDivSizeSetConfig: currentMajorDivSize,
      fineGridSetConfig: currentFineGrid,
      numberOfVisibleChannelsSetConfig: currentNumberOfVisibleChannels,
      displayedChannelsArrSetConfig: currentDisplayedChannelsArr,
    };

    // *: Reset config
    this.setConfig(setConfigObject);

    for (let i = 0; i < allIndexStripSvg.length; i += 1) {
      allIndexStripSvg[i] = optimizeSVG(allIndexStripSvg[i]);
    }

    for (let i = 0; i < allStripSvg.length; i += 1) {
      allStripSvg[i] = optimizeSVG(allStripSvg[i]);
    }

    // let timeOut = 0;
    // _.forEach(allStripSvg, (element, index) => {
    //   setTimeout(() => {
    //     const blob = new Blob([optimizeSVG(element)], { type: 'image/svg+xml; charset=utf-8' });
    //     FileSaver.saveAs(blob, `ECG-${index + 1}.svg`, true);
    //   }, timeOut);
    //   timeOut += 100;
    // });

    return { allIndexStripSvg, allStripSvg };
  };

  ECGChart.prototype.pickDate = function () {
    if (this.propsFunction.handlePickDate) {
      const diffSeconds = this.clickedOffsetX / 250;
      this.propsFunction.handlePickDate(diffSeconds);
    }
  };

  ECGChart.prototype.getRemainSeconds = function () {
    const { sampleLength } = this.config;
    const remainSamples = sampleLength - this.offsetX;
    const remainSeconds = remainSamples / 250;
    return remainSeconds;
  };

  ECGChart.prototype.handleCapture = function (captureDuration: any, highlightChannel: any) {
    const { samplingRate } = this.config;

    const sampleMarkRange = samplingRate * captureDuration;
    const sampleMark = {
      channelIndex: this.displayedChannelsArr[highlightChannel],
      startSample: this.offsetX,
      endSample: this.offsetX + sampleMarkRange - 1,
      sampleMarkRange,
    };

    this.ecgView.sampleMarksArray.length = 0;

    this.ecgView.drawSampleMark(this.offsetX, sampleMark);
    this.propsFunction.updateCompletedStatus(false, true);

    const { allIndexStripSvg } = this.exportAll(true);

    const widgets = this.getWidgets();
    /**
     * The sample-mark entries among the supplied annotation widgets. Filters the widget list down to those whose type matches the SAMPLE_MARK annotation type.
     *
     * @returns Widgets representing sample marks.
     */
    const sampleMarkWidgets = _.filter(widgets, (x) => x.type === ANNOTATION_TYPE.SAMPLE_MARK);

    return {
      draw: sampleMarkWidgets,
      allIndexStripSvg,
    };
  };

  ECGChart.prototype.getJSONObject = function () {
    const { samplingRate, adcZero, adcGain, LFF, HFF, prevChanIdx } = this.config;

    const { speed, gain } = this.beforeZoomValue
      ? this.beforeZoomValue
      : this.beforeViewAllValue
        ? this.beforeViewAllValue
        : this.config;

    const displayedChannelsArr = this.beforeZoomValue
      ? this.beforeZoomValue.displayedChannelsArr
      : this.displayedChannelsArr;

    const annoArr = this.annotation.getJSONArr();
    const viewArr = this.ecgView.getJSONObject();

    const result = {
      object: [...annoArr, ...viewArr],
      dataECG: {
        samples: this.rawDataFromNetwork,
        samplingRate,
        offsetX: this.offsetX,
        adcZero,
        adcGain,
        speed,
        gain,
        LFF,
        HFF,
        prevChanIdx,
        displayedChannelsArr,
      },
    };

    result.object.push(this.markers.getJSONObject());

    return JSON.stringify(result);
  };
})();

export { ECGChart };
export default { ECGChart };
